import { useEffect, useRef } from 'react';
import { FormProvider } from 'react-hook-form';
import { useParams } from 'react-router';
import { Button, Center, Column, CustomSpinner, Row, Text } from '~/commonComponents';
import {
  AutoSaveStatus,
  EarningBenefitForm,
  GeneralErrorMessage,
  InfoSection,
  JobDetailsFormMapper,
  useJobDetailsForm,
} from '../JobDetailsForm';
import { ScheduleForm } from '../JobDetailsForm/components/ScheduleInfo';
import {
  CreatePermanentJobPostingFormSection,
  CreatePermanentJobPostingSteps,
  TCreatePermanentJobPostingPageRoutes,
} from './Types';
import {
  useAutoSaveDraftJobPost,
  useAutoScrollToSection,
  useDraftJobPost,
  useSaveJobPost,
} from './hooks';
import { firePermPostJobWorkDetailsSubmitted } from '~/posthogs/events';

export const WorkDetailsPage = () => {
  const { id } = useParams<TCreatePermanentJobPostingPageRoutes>();
  const { draft, isLoading } = useDraftJobPost(id);

  const scheduleRef = useRef<HTMLDivElement>(null);
  const earningsAndBenefitsRef = useRef<HTMLDivElement>(null);

  const controller = useJobDetailsForm({
    initialValues: draft,
  });

  useEffect(() => {
    if (draft) {
      controller.reset(JobDetailsFormMapper.fromEntity(draft));
    }
  }, [draft, controller]);

  useAutoScrollToSection({
    sectionRefs: {
      [CreatePermanentJobPostingFormSection.Schedule]: scheduleRef,
      [CreatePermanentJobPostingFormSection.EarningsAndBenefits]: earningsAndBenefitsRef,
    },
    isReady: !isLoading && !!draft,
  });

  const { isPending, hasFormErrors, onSave } = useSaveJobPost(controller, id, {
    targetStep: CreatePermanentJobPostingSteps.JobDescription,
    triggers: [
      'benefits',
      'benefitsOther',
      'compensationType',
      'expectedDays',
      'maxRate',
      'minRate',
      'payType',
      'scheduleType',
    ],
    onTrackSuccess: (postingId, formData) => {
      firePermPostJobWorkDetailsSubmitted({
        postingId,
        professionType: formData.professionType,
      });
    },
  });
  const {
    isLoading: isAutoSaveLoading,
    isShowSavedMessage,
    lastSavedAt,
  } = useAutoSaveDraftJobPost(controller, id);

  if (isLoading) {
    return (
      <Center marginBlock={48}>
        <CustomSpinner isLoading size={24} />
      </Center>
    );
  }

  return (
    <>
      <AutoSaveStatus visible={isShowSavedMessage} date={lastSavedAt} />
      <Column width="60%" alignSelf="center">
        <Text bold fontSize={22} marginBottom={14} marginLeft={24}>
          Step 2: Work Details
        </Text>
        {hasFormErrors && <GeneralErrorMessage />}
        <FormProvider {...controller}>
          <Column gap={16}>
            <InfoSection ref={scheduleRef} icon="calendar-solid" title="Schedule">
              <ScheduleForm control={controller.control} errors={controller.formState.errors} />
            </InfoSection>
            <InfoSection
              ref={earningsAndBenefitsRef}
              icon="money-pay-solid"
              title="Earnings and Benefits"
            >
              <EarningBenefitForm />
            </InfoSection>
          </Column>
        </FormProvider>
        <Row justifyContent="center" marginTop={24}>
          <Button text="Next" onClick={onSave} isLoading={isPending || isAutoSaveLoading} />
        </Row>
      </Column>
    </>
  );
};
